import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Compass, Repeat, Target, TrendingUp,
  Sparkles, Layout, PenTool, Share2, Quote,
} from "lucide-react";
import Reveal from "@/components/Reveal";

const IMG = {
  hero: "https://customer-assets-cm19k8pv.emergentagent.net/job_strategy-led-2/artifacts/7dwb4w3p_DB%20With%20client.jpg",
  founder: "https://customer-assets-cm19k8pv.emergentagent.net/job_strategy-led-2/artifacts/d9l6i6dx_David%20Office%202.jpg",
  web: "https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1100&q=80",
  work1: "https://images.unsplash.com/photo-1611926653458-09294b3142bf?auto=format&fit=crop&w=900&q=80",
  work2: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1100&q=80",
};

const MINT = "#DDF2E7";
const BUTTER = "#FFF1C9";
const SKY = "#E3EEFF";
const LILAC = "#ECE9FF";
// Deeper, non-orange accents used only for hover borders
const ACCENT = { [MINT]: "#4FAE8B", [BUTTER]: "#C9A227", [SKY]: "#5B9BD5", [LILAC]: "#7C6CF0" };

const PRIMARY_SERVICES = [
  { icon: Share2, slug: "social-media-management", title: "Social Media Management", copy: "Done-for-you content, strategy and support so you can stay consistent and show up with confidence and clarity.", tint: MINT },
  { icon: PenTool, slug: "content-strategy", title: "Content Strategy", copy: "Clear direction, content pillars and a practical strategy so every post has a purpose.", tint: BUTTER },
  { icon: Layout, slug: "website-design", title: "Website Design", copy: "Premium, strategic websites that act as the centre of your marketing rather than a static brochure.", tint: SKY },
  { icon: Sparkles, slug: "ai-consultancy", title: "AI Consultancy", copy: "Practical AI support, training and workflows that help you save time without losing the human touch.", tint: LILAC },
];

const SECONDARY = ["Social Media Design", "Virtual Marketing Support", "Creative Campaign Support"];

const PAIN = [
  "You are posting on social media, but not sure what you are saying.",
  "Your website does not reflect your business.",
  "AI feels useful, but overwhelming.",
  "Your marketing lacks structure.",
  "You are busy, but not moving forward.",
];

const WHY = [
  { icon: Compass, title: "Expert Direction", copy: "Know what to post, why it matters and how it supports your business.", tint: SKY },
  { icon: Repeat, title: "Consistent Delivery", copy: "Stay visible without relying on last-minute content or internal capacity.", tint: MINT },
  { icon: Target, title: "Strategy with Accountability", copy: "Make decisions based on clear priorities, feedback and performance.", tint: BUTTER },
  { icon: TrendingUp, title: "Built to Scale", copy: "Create systems that can grow with your business, team and goals.", tint: LILAC },
];

const WORK = [
  { img: IMG.web, label: "Website & Landing Pages", copy: "A clearer journey and stronger enquiries.", tint: SKY },
  { img: IMG.work2, label: "Analytics & Reporting", copy: "Reporting that guides the next decision.", tint: MINT },
  { img: IMG.work1, label: "Social & Content", copy: "Every post with a clear purpose.", tint: BUTTER },
];

export default function Home3() {
  useEffect(() => {
    document.title = "Track Digital | Style Meets Strategy";
  }, []);

  return (
    <main data-testid="home3-page" className="overflow-hidden">
      {/* Hero - split: text left, image boxed right */}
      <section className="pt-28 md:pt-32 pb-12 md:pb-16" data-testid="hero-section">
        <div className="td-container grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <span className="td-label block mb-6">Style Meets Strategy</span>
            <h1 className="text-4xl sm:text-5xl lg:text-[4.2rem] font-semibold leading-[1.02] text-black">
              Marketing with <span className="td-hl td-hl-sky">direction</span>.
            </h1>
            <p className="text-lg md:text-xl text-[#3a3a44] leading-relaxed mt-7 max-w-lg">
              Track Digital helps ambitious businesses build structured, consistent and measurable
              marketing. We do not just post content. We build the direction behind it.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-9">
              <Link to="/contact" data-testid="hero-primary-cta" className="td-btn td-btn-primary">
                Book a Discovery Call <ArrowRight size={18} />
              </Link>
              <Link to="/services" data-testid="hero-secondary-cta" className="td-btn td-btn-outline">Explore Services</Link>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative rounded-[1.75rem] overflow-hidden bg-[#eceae7] img-zoom">
              <div className="aspect-square w-full">
                <img
                  src={IMG.hero}
                  alt="David Ball reviewing a social media strategy with a client"
                  className="w-full h-full object-cover object-center"
                  loading="eager"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Problem - cohesive, decluttered pains */}
      <section className="pt-16 md:pt-24 pb-16 md:pb-24" data-testid="problem-section">
        <div className="td-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <Reveal className="lg:col-span-5">
              <span className="td-label block mb-6">The Problem</span>
              <h2 className="text-3xl md:text-5xl font-semibold leading-[1.06]">
                Visibility is not the issue. Direction is.
              </h2>
              <p className="text-[#4a4a4a] text-lg leading-relaxed mt-6">
                Businesses rarely struggle because they are invisible. They struggle because their marketing
                lacks direction and clarity.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                {["Clarity", "Structure", "Measurable progress"].map((w) => (
                  <span key={w} className="rounded-full border border-[#e5e5e5] px-4 py-2 text-sm font-medium text-black">{w}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={120} className="lg:col-span-7">
              <div className="rounded-3xl p-8 md:p-10" style={{ background: SKY }}>
                <p className="td-label mb-4">Sound familiar?</p>
                <ul className="divide-y divide-black/10">
                  {PAIN.map((p) => (
                    <li key={p} className="py-4 text-[#2b2b2b] text-lg leading-snug">{p}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services - pastel cards, per-card pastel hover frame */}
      <section className="py-16 md:py-24 bg-[#fafafa]" data-testid="services-overview">
        <div className="td-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="td-label block mb-5">What We Do</span>
            <h2 className="text-3xl md:text-5xl font-semibold leading-[1.1]">
              Strategy-led marketing, <span className="td-hl td-hl-mint">delivered properly</span>.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PRIMARY_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={(i % 2) * 90}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="td-frame-card group relative block rounded-3xl p-8 md:p-9 h-full overflow-hidden hover:-translate-y-1"
                    style={{ background: s.tint, "--frame": ACCENT[s.tint] }}
                  >
                    <div className="flex items-start justify-between mb-7">
                      <span className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                        <Icon size={22} className="text-[#1a1a1a]" strokeWidth={2} />
                      </span>
                      <ArrowUpRight size={22} className="text-black/30 group-hover:text-black/70 transition-colors" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-semibold mb-2.5">{s.title}</h3>
                    <p className="text-[#4a4a55] leading-relaxed td-measure">{s.copy}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-3">
            <span className="td-label mr-2">Also</span>
            {SECONDARY.map((s) => (
              <span key={s} className="rounded-full border border-[#e5e5e5] px-4 py-2 text-sm text-[#4a4a4a]">{s}</span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Capability band - full bleed lilac */}
      <section className="py-16 md:py-24" style={{ background: LILAC }}>
        <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <span className="td-label block mb-5">Built Around You</span>
            <h2 className="text-3xl md:text-4xl font-semibold leading-[1.1]">
              Boutique attention, structured to grow with you.
            </h2>
            <p className="text-[#4a4a55] text-lg leading-relaxed mt-5 max-w-lg">
              Every business is different. Whether you&apos;re just getting started or scaling up, we
              shape our support around your goals, your capacity and the way you work, adapting as
              your business grows.
            </p>
          </Reveal>
          <div className="lg:col-span-6 grid grid-cols-2 gap-y-10 gap-x-6">
            {[
              { n: "7", l: "Connected service areas" },
              { n: "3", l: "Ways to work with us" },
              { n: "1:1", l: "Direct founder contact" },
              { n: "100%", l: "Strategy led" },
            ].map((s) => (
              <Reveal key={s.l}>
                <p className="font-head text-5xl md:text-6xl font-semibold text-[#1a1a1a]">{s.n}<span className="text-[#7C6CF0]">.</span></p>
                <p className="text-sm text-[#5a5a66] mt-2 max-w-[11rem]">{s.l}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder - split */}
      <section className="py-16 md:py-24" data-testid="founder-section">
        <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden max-w-md img-zoom">
              <img src={IMG.founder} alt="David Ball, Founder of Track Digital" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7">
            <span className="td-label block mb-6">Founder Welcome</span>
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight mb-6">
              You work directly with the person behind the strategy.
            </h2>
            <p className="text-lg text-[#4a4a4a] leading-relaxed td-measure mb-5">
              As founder of Track Digital, I work directly with every client, combining strategic
              thinking, creative direction and practical systems to help businesses build clearer,
              more confident marketing.
            </p>
            <p className="text-lg text-[#4a4a4a] leading-relaxed td-measure mb-8">
              I enjoy working closely with businesses to understand how they operate, what they&apos;re
              trying to achieve and how marketing can genuinely support those goals. Most businesses
              don&apos;t need more noise. They need clearer thinking, better systems and marketing that
              feels manageable. That&apos;s the work I care about.
            </p>
            <p className="font-head text-lg font-semibold text-black">David Ball</p>
            <p className="text-[#8a8a8a] mb-8">Founder, Track Digital</p>
            <Link to="/about" className="inline-flex items-center gap-2 text-[#1a1a1a] font-medium hover:text-[#7C6CF0] transition-colors">
              Work directly with David <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* AI highlight - contained mint card */}
      <section className="td-container pb-16 md:pb-24">
        <Reveal className="rounded-3xl p-8 md:p-16" style={{ background: MINT }} data-testid="ai-highlight">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="td-label block mb-6">AI Consultancy</span>
              <h2 className="text-3xl md:text-4xl font-semibold leading-tight mb-6">AI that makes business simpler.</h2>
              <p className="text-[#3a4a44] text-lg leading-relaxed mb-8">
                We help you understand where AI fits, where it does not and how to build practical workflows
                that support real people doing real work. AI should support people, not replace them.
              </p>
              <Link to="/services/ai-consultancy" className="td-btn td-btn-primary">
                Explore AI Consultancy <ArrowRight size={18} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {["Reduced admin", "Reusable prompts", "Better workflows", "More confident teams"].map((t) => (
                <div key={t} className="rounded-2xl bg-white/80 p-5">
                  <Sparkles size={20} className="text-[#4FAE8B] mb-3" />
                  <p className="text-[#1a1a1a] text-sm font-medium">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Website design - split */}
      <section className="pb-16 md:pb-24" data-testid="website-highlight">
        <div className="td-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <span className="td-label block mb-6">Website Design</span>
            <h2 className="text-3xl md:text-4xl font-semibold leading-tight mb-6">
              Websites built with strategy, style and purpose.
            </h2>
            <p className="text-lg text-[#4a4a4a] leading-relaxed td-measure mb-8">
              Websites are no longer digital brochures. They are business tools. Every website should help
              people understand what you do, trust your business and take the next step.
            </p>
            <Link to="/services/website-design" className="td-btn td-btn-primary">
              Explore Website Design <ArrowRight size={18} />
            </Link>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="aspect-[16/11] rounded-3xl overflow-hidden border border-[#eee] img-zoom">
              <img src={IMG.web} alt="Website design work in progress" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonial - light, editorial quote */}
      <section className="py-16 md:py-24 bg-[#fafafa]" data-testid="testimonial-section">
        <div className="td-container max-w-4xl">
          <Reveal>
            <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-8" style={{ background: BUTTER }}>
              <Quote size={26} className="text-[#1a1a1a]" />
            </span>
            <blockquote className="font-head text-2xl md:text-4xl font-semibold text-black leading-[1.24]">
              Track Digital has helped us build a consistent and structured social media presence, increasing
              awareness, engagement and website traffic through targeted campaigns. Reporting is always clear
              and timely, and they are responsive, professional and easy to work with.
            </blockquote>
            <div className="mt-8 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center font-head font-semibold text-sm">PS</div>
              <div>
                <p className="font-head font-semibold text-black">Peter Sutton</p>
                <p className="text-[#6b6b6b] text-sm">Operations Manager, Queen Square Imaging Centre</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why outsource - pastel cards */}
      <section className="py-16 md:py-24" data-testid="why-outsource">
        <div className="td-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="td-label block mb-5">Why Track</span>
            <h2 className="text-3xl md:text-5xl font-semibold leading-[1.1]">
              Why <span className="td-hl td-hl-lilac">outsource</span> your social media and digital marketing?
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={i * 80}>
                  <div className="rounded-3xl p-8 h-full" style={{ background: w.tint }}>
                    <span className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center mb-6 shadow-sm">
                      <Icon size={22} className="text-[#1a1a1a]" strokeWidth={2} />
                    </span>
                    <h3 className="text-lg font-semibold mb-3">{w.title}</h3>
                    <p className="text-[#4a4a55] text-[15px] leading-relaxed">{w.copy}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Selected work - clean cards, pastel hover frame */}
      <section className="py-16 md:py-24 bg-[#fafafa]" data-testid="selected-work">
        <div className="td-container">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="td-label block mb-5">Selected Work</span>
              <h2 className="text-3xl md:text-5xl font-semibold leading-[1.1] mb-3">Strategy in action.</h2>
              <p className="text-lg text-[#6b6b6b] leading-relaxed">
                A look at how structured thinking becomes visible, useful digital work.
              </p>
            </div>
            <Link to="/work" className="td-btn td-btn-outline shrink-0">View Selected Work <ArrowUpRight size={18} /></Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {WORK.map((w, i) => (
              <Reveal key={w.label} delay={i * 90}>
                <Link to="/work" className="group block">
                  <div className="td-frame-card relative aspect-[5/4] rounded-3xl overflow-hidden bg-white" style={{ "--frame": ACCENT[w.tint] }}>
                    <img src={w.img} alt={w.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <p className="td-label block mt-5 mb-2">{w.label}</p>
                  <p className="text-[#4a4a4a] group-hover:text-black transition-colors">{w.copy}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - lilac */}
      <section className="td-container py-16 md:py-24">
        <Reveal className="rounded-3xl px-8 py-16 md:px-16 md:py-24 relative overflow-hidden" style={{ background: LILAC }} data-testid="cta-section">
          <div className="absolute -right-16 -bottom-20 w-72 h-72 rounded-full" style={{ background: "rgba(124,108,240,0.12)" }} />
          <div className="relative max-w-3xl">
            <span className="td-label block mb-6">What We Do</span>
            <h2 className="text-3xl md:text-5xl font-semibold leading-[1.08] mb-6">
              We bring the strategy, structure and support your marketing needs to move properly.
            </h2>
            <p className="text-[#4a4a55] text-lg leading-relaxed td-measure mb-10">
              From social media management and content strategy to AI, websites and digital systems,
              Track Digital helps businesses build clearer marketing, stronger online visibility and
              the confidence to keep moving forward.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to="/contact" data-testid="cta-primary" className="td-btn td-btn-primary">
                Book a Discovery Call <ArrowRight size={18} />
              </Link>
              <Link to="/services" data-testid="cta-secondary" className="td-btn td-btn-outline">Explore Services</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
